import React, { useState } from "react";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const TrackingMetricsList = ({ data = [], summary = {}, filters = {} }) => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const filteredData = data.filter((item) => {
    const matchesCategory = !filters.category || item.category === filters.category;
    return matchesCategory;
  });

  const totalRows = filteredData.length;
  const start = totalRows > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  const columns = [
    {
      name: "Metric Category",
      selector: (row) => row.category || "—",
      sortable: true,
      width: "250px",
      cell: (row) => <span className="text-capitalize">{row.category || "—"}</span>
    },
    {
      name: "Weekly",
      selector: (row) => row.weekly || 0,
      sortable: true,
    },
    {
      name: "Monthly",
      selector: (row) => row.monthly || 0,
      sortable: true,
    },
    {
      name: "Yearly",
      selector: (row) => row.yearly || 0,
      sortable: true,
    },
    {
      name: "Monthly Comparison (%)",
      selector: (row) => row.monthly_comparison || "0%",
      sortable: true,
    },
    {
      name: "Yearly Comparison (%)",
      selector: (row) => row.yearly_comparison || "0%",
      sortable: true,
    },
  ];

  const handleExportPDF = () => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(18);
    doc.text("Tracking Metrics Report", 14, 15);
    
    // Summary Section
    doc.setFontSize(12);
    doc.text("Conversion Summary", 14, 25);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Residential: ${summary.residential || 0}`, 14, 32);
    doc.text(`DRP: ${summary.drp || 0}`, 60, 32);
    doc.text(`Home Visitation: ${summary.hv || 0}`, 100, 32);
    doc.text(`Declined: ${summary.declined || 0}`, 150, 32);
    
    // Table Reset
    doc.setTextColor(0);
    const tableColumn = ["Category", "Weekly", "Monthly", "Yearly", "M. Comparison", "Y. Comparison"];
    const tableRows = filteredData.map(d => [
      d.category || "—",
      d.weekly || 0,
      d.monthly || 0,
      d.yearly || 0,
      d.monthly_comparison || "0%",
      d.yearly_comparison || "0%"
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 40,
    });
    doc.save("tracking_metrics_report.pdf");
  };

  const getCSVData = () => {
    const headers = ["Category", "Weekly", "Monthly", "Yearly", "M. Comparison", "Y. Comparison"];
    const rows = filteredData.map(d => [
      d.category || "—",
      d.weekly || 0,
      d.monthly || 0,
      d.yearly || 0,
      d.monthly_comparison || "0%",
      d.yearly_comparison || "0%"
    ]);

    return [
      ["Conversion Summary"],
      ["Residential", summary.residential || 0],
      ["DRP", summary.drp || 0],
      ["HV", summary.hv || 0],
      ["Declined", summary.declined || 0],
      [], // blank line
      headers,
      ...rows
    ];
  };

  const CustomPagination = ({ onChangePage, onChangeRowsPerPage, currentPage, rowsPerPage, rowCount }) => {
    const totalPages = Math.ceil(rowCount / rowsPerPage);
    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>Rows per page:</span>
          <select className="form-select form-select-sm" style={{ width: "80px" }} value={rowsPerPage} onChange={(e) => onChangeRowsPerPage(Number(e.target.value), currentPage)}>
            {[5, 10, 15, 20, 25].map((option) => (<option key={option} value={option}>{option}</option>))}
          </select>
        </div>
        <div className="d-flex align-items-center gap-1">
          <button className="btn btn-sm border" onClick={() => onChangePage(currentPage - 1, rowCount)} disabled={currentPage === 1}>
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>Page {currentPage} of {totalPages}</span>
          <button className="btn btn-sm border" onClick={() => onChangePage(currentPage + 1, rowCount)} disabled={currentPage === totalPages}>
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="d-flex align-items-center gap-2 position-relative">
          <div className="dropdown">
            <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
              <i className="ri-download-2-line me-1"></i> Export
            </button>
            <ul className="dropdown-menu shadow-sm">
              <li><button className="dropdown-item" onClick={handleExportPDF}><i className="ri-file-pdf-line me-1 text-danger"></i> PDF</button></li>
              <li><CSVLink data={getCSVData()} filename="tracking_metrics.csv" className="dropdown-item"><i className="ri-file-excel-line me-1 text-success"></i> CSV</CSVLink></li>
            </ul>
          </div>
        </div>
        <h6 className="mb-0 text-center text-muted flex-grow-1">Showing {start}-{end} of {totalRows} metrics</h6>
      </div>
      <div className="card-body p-2">
        <DataTable
          columns={columns}
          data={filteredData}
          pagination
          paginationPerPage={rowsPerPage}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={setRowsPerPage}
          onChangePage={setCurrentPage}
          paginationComponent={CustomPagination}
          highlightOnHover
          striped
          responsive
          noDataComponent={<div className="p-3 text-muted">No metrics data found</div>}
        />
        
        <div className="mt-4 p-3 border rounded bg-light">
          <h5>Conversion Summary</h5>
          <div className="row mt-3">
            <div className="col-md-3">
              <div className="card text-center p-3 shadow-sm">
                <h6>Residential Clients</h6>
                <h3 className="text-primary">{summary.residential || 0}</h3>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center p-3 shadow-sm">
                <h6>DRP Clients</h6>
                <h3 className="text-success">{summary.drp || 0}</h3>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center p-3 shadow-sm">
                <h6>HV Clients</h6>
                <h3 className="text-info">{summary.hv || 0}</h3>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center p-3 shadow-sm">
                <h6>Declined (Policy)</h6>
                <h3 className="text-danger">{summary.declined || 0}</h3>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackingMetricsList;
